import { Routes } from '@angular/router';
import { marketplaceFeatureGuard } from '../guards/marketplace-feature.guard';

/**
 * Shared marketplace children — browse, library, pack detail, pack studio.
 * Mounted under every experience (`education` / `business` / `creator` / `learn`)
 * as `/marketplace/*`.
 */
const MARKETPLACE_SHARED_CHILDREN: Routes = [
  {
    path: '',
    pathMatch: 'full',
    data: { ownerScope: 'workspace' },
    loadComponent: () =>
      import('../../pages/assessments/marketplace/catalog/assessment-marketplace-catalog.page').then(
        (m) => m.AssessmentMarketplaceCatalogPage,
      ),
  },
  {
    path: 'library',
    data: { ownerScope: 'workspace' },
    loadComponent: () =>
      import('../../pages/assessments/marketplace/favorites/assessment-marketplace-favorites.page').then(
        (m) => m.AssessmentMarketplaceFavoritesPage,
      ),
  },
  {
    path: 'pack/:packGuid',
    data: { ownerScope: 'workspace' },
    loadComponent: () =>
      import('../../pages/assessments/marketplace/detail/assessment-marketplace-detail.page').then(
        (m) => m.AssessmentMarketplaceDetailPage,
      ),
  },
  // Publisher authoring follows Assessment owner scope. Workspace mutations are
  // authorized by the Assessment backend's workspace-manager check.
  {
    path: 'packs',
    data: { ownerScope: 'workspace' },
    loadComponent: () =>
      import('../../pages/assessments/marketplace/studio/assessment-marketplace-studio.page').then(
        (m) => m.AssessmentMarketplaceStudioPage,
      ),
  },
  {
    path: 'packs/versions',
    data: { ownerScope: 'workspace' },
    loadComponent: () =>
      import('../../pages/assessments/marketplace/studio/assessment-marketplace-studio.page').then(
        (m) => m.AssessmentMarketplaceStudioPage,
      ),
  },
  {
    path: 'packs/editor',
    data: { minimalShell: true, ownerScope: 'workspace' },
    loadComponent: () =>
      import('../../pages/assessments/marketplace/studio/assessment-marketplace-studio.page').then(
        (m) => m.AssessmentMarketplaceStudioPage,
      ),
  },
  {
    path: 'admin',
    data: { ownerScope: 'workspace' },
    loadComponent: () =>
      import('../../pages/assessments/marketplace/admin/assessment-marketplace-admin.page').then(
        (m) => m.AssessmentMarketplaceAdminPage,
      ),
  },
];

function marketplaceShell(children: Routes): Routes {
  return [
    {
      path: 'marketplace',
      // Guarded on the shell so every child is covered: with the product feature
      // off the backend answers these pages with 403, so a deep link goes home
      // instead of rendering an empty catalog. Mirrors the nav-item gate.
      canActivate: [marketplaceFeatureGuard],
      loadComponent: () =>
        import(
          '../../pages/workspaces/shared/marketplace/marketplace-shell.component'
        ).then((m) => m.MarketplaceShellComponent),
      // Wildcard appended here so it is always the LAST child: any unknown or
      // guid-less marketplace URL lands on browse instead of the 404 placeholder.
      children: [...children, { path: '**', redirectTo: '' }],
    },
  ];
}

/** Marketplace browse + library + pack detail + pack studio for every workspace. */
export const MARKETPLACE_ROUTES: Routes = marketplaceShell(MARKETPLACE_SHARED_CHILDREN);
